import { useState, useEffect, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QueueProvider } from './contexts/QueueContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LandingPage } from './components/LandingPage';
import { AuthPage } from './components/AuthPage';
import { ConnectionStatus } from './components/ConnectionStatus';
import { projectId, publicAnonKey } from './utils/supabase/info';

const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const StaffDashboard = lazy(() => import('./components/StaffDashboard').then(m => ({ default: m.StaffDashboard })));
const StudentDashboard = lazy(() => import('./components/StudentDashboard').then(m => ({ default: m.StudentDashboard })));
const EncoderDashboard = lazy(() => import('./components/EncoderDashboard').then(m => ({ default: m.EncoderDashboard })));
const CashierDashboard = lazy(() => import('./components/CashierDashboard').then(m => ({ default: m.CashierDashboard })));
const MonitorDisplay = lazy(() => import('./components/MonitorDisplay'));

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-d4ddf86c`;

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const { user, loading } = useAuth();
  const [isBackendConnected, setIsBackendConnected] = useState(true);
  const [isMonitorMode, setIsMonitorMode] = useState(false);
  const [showLanding, setShowLanding] = useState(true);

  // Check for monitor mode in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'monitor') {
      setIsMonitorMode(true);
    }
  }, []);

  // If in monitor mode, show monitor display without authentication
  if (isMonitorMode) {
    return <Suspense fallback={<LoadingFallback />}><MonitorDisplay /></Suspense>;
  }

  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <>
      <ConnectionStatus onStatusChange={setIsBackendConnected} />
      {!user ? (
        showLanding ? (
          <LandingPage onGetStarted={() => setShowLanding(false)} />
        ) : (
          <AuthPage onBackToLanding={() => setShowLanding(true)} />
        )
      ) : (
        <Suspense fallback={<LoadingFallback />}>
          {user.role === 'administrator' && <AdminDashboard />}
          {user.role === 'staff' && <StaffDashboard />}
          {user.role === 'student' && <StudentDashboard />}
          {user.role === 'encoder' && <EncoderDashboard />}
          {user.role === 'cashier' && <CashierDashboard />}
          {!['administrator', 'staff', 'student', 'encoder', 'cashier'].includes(user.role) && (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
              <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
                <h2 className="text-xl font-bold text-red-600 mb-2">Invalid Role</h2>
                <p className="text-muted-foreground mb-4">Your account role "{user.role}" is not recognized. Please contact an administrator to fix your account.</p>
                <button onClick={() => { localStorage.clear(); window.location.reload(); }} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">Logout</button>
              </div>
            </div>
          )}
        </Suspense>
      )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <QueueProvider>
          <AppContent />
        </QueueProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}